import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useInvoice } from '@/hooks/useInvoice';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SupportTicketConsole } from '@/components/SupportTicketConsole';
import { MessageSquare } from 'lucide-react';
import { db } from '@/lib/firebase';
import { doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { generateSecret, verifyTOTP } from '@/lib/totp';
import { compressLogo, formatCurrency } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Crown, AlertTriangle, User, Mail, Building2, Plus, Pencil, Trash2, Star, Users, Package, Upload, ImageIcon, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ALL_CURRENCIES } from '@/types/invoice';
import type { Business, Customer, InventoryItem, Tax, TaxDiscountType, Currency } from '@/types/invoice';
import { isValidEmail, isValidPhone, isValidStripeLink, isValidPaypalLink } from '@/lib/validation';



export default function Profile() {
  const { user, userProfile, deleteAccount, refreshUserProfile } = useAuth();
  const { isPro } = useSubscription();
  const { toast } = useToast();

  // Scroll to support tickets if hash is present
  useEffect(() => {
    if (window.location.hash === '#support-tickets') {
      const el = document.getElementById('support-tickets');
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    }
  }, []);

  // 2FA State
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [setupStep, setSetupStep] = useState<'idle' | 'scan' | 'verify'>('idle');
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState<string | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);

  // Default Currency State
  const [isSavingCurrency, setIsSavingCurrency] = useState(false);

  const handleUpdateDefaultCurrency = async (val: string) => {
    if (!user) return;
    setIsSavingCurrency(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        defaultCurrency: val as Currency
      });
      toast({
        title: 'Currency Updated',
        description: `Default currency successfully set to ${val}.`,
      });
      await refreshUserProfile();
    } catch (err) {
      console.error('Error updating default currency:', err);
      toast({
        title: 'Error',
        description: 'Failed to update default currency.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingCurrency(false);
    }
  };
  const { 
    businesses, createBusiness, updateBusiness, deleteBusiness,
    customers, createCustomer, updateCustomer, deleteCustomer,
    inventoryItems = [], createInventoryItem, updateInventoryItem, deleteInventoryItem
  } = useInvoice();
  const navigate = useNavigate();

  
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Business dialog state
  const [showBusinessDialog, setShowBusinessDialog] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [businessForm, setBusinessForm] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    website: '',
    taxNumber: '',
    bankDetails: '',
    logoUrl: '',
    isDefault: false,
    stripePaymentLink: '',
    paypalLink: '',
    disableAutoApplyPaymentLinks: false,
  });
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload an image file.',
        variant: 'destructive',
      });
      return;
    }

    setLogoUploading(true);
    try {
      const base64Url = await compressLogo(file);
      setBusinessForm(prev => ({ ...prev, logoUrl: base64Url }));
      toast({
        title: 'Logo Uploaded',
        description: 'Business logo has been processed and saved successfully.',
      });
    } catch (error) {
      console.error('Error processing logo:', error);
      toast({
        title: 'Upload Failed',
        description: 'Failed to process logo image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setBusinessForm(prev => ({ ...prev, logoUrl: '' }));
    toast({
      title: 'Logo Removed',
      description: 'Business logo has been removed.',
    });
  };


  const handleAddBusiness = () => {
    setEditingBusiness(null);
    setBusinessForm({
      name: '',
      address: '',
      email: '',
      phone: '',
      website: '',
      taxNumber: '',
      bankDetails: '',
      logoUrl: '',
      isDefault: businesses.length === 0, // First business is default
      stripePaymentLink: '',
      paypalLink: '',
      disableAutoApplyPaymentLinks: false,
    });
    setShowBusinessDialog(true);
  };

  const handleEditBusiness = (business: Business) => {
    setEditingBusiness(business);
    setBusinessForm({
      name: business.name,
      address: business.address || '',
      email: business.email,
      phone: business.phone || '',
      website: business.website || '',
      taxNumber: business.taxNumber || '',
      bankDetails: business.bankDetails,
      logoUrl: business.logoUrl || '',
      isDefault: business.isDefault,
      stripePaymentLink: (business as any).stripePaymentLink || '',
      paypalLink: (business as any).paypalLink || '',
      disableAutoApplyPaymentLinks: business.disableAutoApplyPaymentLinks || false,
    });
    setShowBusinessDialog(true);
  };

  const handleSaveBusiness = async () => {
    if (!businessForm.name || !businessForm.email) {
      return;
    }

    if (!isValidEmail(businessForm.email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid business email address.',
        variant: 'destructive',
      });
      return;
    }

    if (businessForm.phone && !isValidPhone(businessForm.phone)) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid phone number (minimum 7 digits, e.g. +123456789).',
        variant: 'destructive',
      });
      return;
    }

    if (businessForm.stripePaymentLink && !isValidStripeLink(businessForm.stripePaymentLink)) {
      toast({
        title: 'Invalid Stripe Link',
        description: 'Stripe links must be secure URLs from stripe.com (e.g. https://buy.stripe.com/...).',
        variant: 'destructive',
      });
      return;
    }

    if (businessForm.paypalLink && !isValidPaypalLink(businessForm.paypalLink)) {
      toast({
        title: 'Invalid PayPal Link',
        description: 'PayPal links must be secure URLs from paypal.me or paypal.com.',
        variant: 'destructive',
      });
      return;
    }

    setSavingBusiness(true);
    try {
      if (editingBusiness) {
        await updateBusiness(editingBusiness.businessId, businessForm);
      } else {
        await createBusiness(businessForm);
      }
      setShowBusinessDialog(false);
    } catch (error) {
      // Error handled by hook
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleDeleteBusiness = async (businessId: string) => {
    if (confirm('Are you sure you want to delete this business profile?')) {
      await deleteBusiness(businessId);
    }
  };

  // Customer dialog state
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  
  // Item Inventory states
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [itemForm, setItemForm] = useState({
    code: '',
    name: '',
    description: '',
    rate: 0 as number | string,
    unit: 'pcs',
  });
  const [savingItem, setSavingItem] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [customerForm, setCustomerForm] = useState({
    name: '',
    companyName: '',
    address: '',
    email: '',
    phone: '',
    taxNumber: '',
    isDefault: false,
  });
  const [savingCustomer, setSavingCustomer] = useState(false);

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setCustomerForm({
      name: '',
      companyName: '',
      address: '',
      email: '',
      phone: '',
      taxNumber: '',
      isDefault: customers.length === 0, // First customer is default
    });
    setShowCustomerDialog(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      name: customer.name,
      companyName: customer.companyName,
      address: customer.address,
      email: customer.email,
      phone: customer.phone,
      taxNumber: customer.taxNumber,
      isDefault: customer.isDefault,
    });
    setShowCustomerDialog(true);
  };

  const handleSaveCustomer = async () => {
    if (!customerForm.name || !customerForm.email) {
      return;
    }

    if (!isValidEmail(customerForm.email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid customer email address.',
        variant: 'destructive',
      });
      return;
    }

    if (customerForm.phone && !isValidPhone(customerForm.phone)) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid phone number (minimum 7 digits, e.g. +123456789).',
        variant: 'destructive',
      });
      return;
    }

    setSavingCustomer(true);
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.customerId, customerForm);
      } else {
        await createCustomer(customerForm);
      }
      setShowCustomerDialog(false);
    } catch (error) {
      // Error handled by hook
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleDeleteCustomer = async (customerId: string) => {
    if (confirm('Are you sure you want to delete this customer profile?')) {
      await deleteCustomer(customerId);
    }
  };

  // Item Inventory handlers
  const handleAddItem = () => {
    setEditingItem(null);
    setItemForm({
      code: '',
      name: '',
      description: '',
      rate: 0,
      unit: 'pcs',
    });
    setShowItemDialog(true);
  };

  const handleEditItem = (item: InventoryItem) => {
    setEditingItem(item);
    setItemForm({
      code: item.code,
      name: item.name,
      description: item.description,
      rate: item.rate,
      unit: item.unit,
    });
    setShowItemDialog(true);
  };

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemForm.code || !itemForm.name) {
      toast({ title: 'Error', description: 'Item code and name are required', variant: 'destructive' });
      return;
    }
    setSavingItem(true);
    try {
      const sanitizedItemForm = {
        ...itemForm,
        rate: Number(itemForm.rate) || 0
      };
      if (editingItem) {
        await updateInventoryItem(editingItem.itemId, sanitizedItemForm);
      } else {
        await createInventoryItem(sanitizedItemForm);
      }
      setShowItemDialog(false);
    } catch (err) {
      // Error handled by hook
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (confirm('Are you sure you want to delete this inventory item or service?')) {
      try {
        await deleteInventoryItem(itemId);
      } catch (err) {
        // Error handled by hook
      }
    }
  };

  // Tax Predefined setup states
  const [showTaxDialog, setShowTaxDialog] = useState(false);
  const [editingTaxIndex, setEditingTaxIndex] = useState<number | null>(null);
  const [taxForm, setTaxForm] = useState<{ label: string; type: TaxDiscountType; value: number | string }>({
    label: '',
    type: 'percentage',
    value: '',
  });
  const [savingTax, setSavingTax] = useState(false);

  const handleAddTax = () => {
    setEditingTaxIndex(null);
    setTaxForm({
      label: '',
      type: 'percentage',
      value: '',
    });
    setShowTaxDialog(true);
  };

  const handleEditTax = (index: number) => {
    if (!userProfile?.predefinedTaxes) return;
    const tax = userProfile.predefinedTaxes[index];
    setEditingTaxIndex(index);
    setTaxForm({
      label: tax.label,
      type: tax.type,
      value: tax.value,
    });
    setShowTaxDialog(true);
  };

  const handleSaveTax = async () => {
    if (!user || !taxForm.label || taxForm.value === '') {
      toast({ title: 'Error', description: 'Label and value are required', variant: 'destructive' });
      return;
    }
    setSavingTax(true);
    try {
      const taxItem: Tax = {
        label: taxForm.label,
        type: taxForm.type,
        value: Number(taxForm.value) || 0,
        enabled: true,
      };

      let updatedTaxes = [...(userProfile?.predefinedTaxes || [])];
      if (editingTaxIndex !== null) {
        updatedTaxes[editingTaxIndex] = taxItem;
      } else {
        updatedTaxes.push(taxItem);
      }

      await updateDoc(doc(db, 'users', user.uid), {
        predefinedTaxes: updatedTaxes
      });

      toast({
        title: 'Tax Setup Saved',
        description: 'Predefined tax rate saved successfully.',
      });

      await refreshUserProfile();
      setShowTaxDialog(false);
    } catch (err) {
      console.error('Error saving predefined tax:', err);
      toast({
        title: 'Error',
        description: 'Failed to save predefined tax',
        variant: 'destructive',
      });
    } finally {
      setSavingTax(false);
    }
  };

  const handleDeleteTax = async (index: number) => {
    if (!user || !userProfile?.predefinedTaxes) return;
    if (confirm('Are you sure you want to delete this predefined tax rate?')) {
      try {
        const updatedTaxes = userProfile.predefinedTaxes.filter((_, i) => i !== index);
        await updateDoc(doc(db, 'users', user.uid), {
          predefinedTaxes: updatedTaxes
        });
        toast({
          title: 'Tax Setup Deleted',
          description: 'Predefined tax rate removed successfully.',
        });
        await refreshUserProfile();
      } catch (err) {
        console.error('Error deleting predefined tax:', err);
        toast({
          title: 'Error',
          description: 'Failed to delete predefined tax',
          variant: 'destructive',
        });
      }
    }
  };

  const handleStartMfaSetup = () => {
    const secret = generateSecret();
    setMfaSecret(secret);
    setSetupStep('scan');
    setTotpCode('');
    setTotpError(null);
  };

  const handleCancelSetup = () => {
    setMfaSecret(null);
    setSetupStep('idle');
    setTotpCode('');
    setTotpError(null);
  };

  const handleConfirmMfaSetup = async () => {
    if (!db || !user || !mfaSecret) return;
    setMfaLoading(true);
    try {
      const isValid = await verifyTOTP(mfaSecret, totpCode);
      if (isValid) {
        // Save TOTP secret in mfa_config/totp subcollection
        await setDoc(doc(db, 'users', user.uid, 'mfa_config', 'totp'), {
          secret: mfaSecret,
          enabled: true,
          updatedAt: new Date()
        });
        
        // Enable MFA in user profile
        await updateDoc(doc(db, 'users', user.uid), {
          mfaEnabled: true
        });

        // Set session verified so they don't get kicked out immediately on next route change
        sessionStorage.setItem(`user_2fa_verified_${user.uid}`, 'true');

        await refreshUserProfile();
        setSetupStep('idle');
        setMfaSecret(null);
        setTotpCode('');
        toast({
          title: '2FA Enabled',
          description: 'Two-factor authentication has been successfully set up for your account.',
        });
      } else {
        setTotpError('Verification code failed. Please check the timing and try again.');
      }
    } catch (error: any) {
      console.error('Error completing MFA setup:', error);
      toast({
        title: 'Error',
        description: 'Failed to save MFA configuration. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!db || !user) return;
    if (!confirm('Are you sure you want to disable two-factor authentication? This will reduce your account security.')) return;
    
    setMfaLoading(true);
    try {
      // Remove secret key
      await deleteDoc(doc(db, 'users', user.uid, 'mfa_config', 'totp'));
      
      // Disable MFA in user profile
      await updateDoc(doc(db, 'users', user.uid), {
        mfaEnabled: false
      });

      // Clear session cache
      sessionStorage.removeItem(`user_2fa_verified_${user.uid}`);

      await refreshUserProfile();
      setTotpCode('');
      toast({
        title: '2FA Disabled',
        description: 'Two-factor authentication has been disabled for your account.',
      });
    } catch (error: any) {
      console.error('Error disabling MFA:', error);
      toast({
        title: 'Error',
        description: 'Failed to disable 2FA. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      return;
    }

    setDeleting(true);
    try {
      await deleteAccount();
      navigate('/');
    } catch (error) {
      // Error handled by useAuth hook
    } finally {
      setDeleting(false);
    }
  };

  if (!user || !userProfile) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="font-heading text-3xl font-bold mb-8">Profile Settings</h1>

      {/* Account Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>Your personal account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={user.photoURL || undefined} />
              <AvatarFallback className="text-2xl">
                {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-xl font-semibold flex items-center gap-2">
                {user.displayName}
                {isPro && (
                  <Badge variant="default" className="ml-2">
                    <Crown className="h-3 w-3 mr-1" />
                    Pro
                  </Badge>
                )}
              </h3>
              <p className="text-muted-foreground">{user.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Display Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={user.displayName || ''}
                  disabled
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={user.email || ''}
                  disabled
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Default Currency</Label>
              <Select
                value={userProfile?.defaultCurrency || 'USD'}
                onValueChange={handleUpdateDefaultCurrency}
                disabled={isSavingCurrency}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Currency" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {ALL_CURRENCIES.map((curr) => (
                    <SelectItem key={curr.code} value={curr.code}>
                      {curr.code} - {curr.name} ({curr.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="text-sm text-muted-foreground mt-4">
            <p>Member since: {userProfile.createdAt.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</p>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
          <CardDescription>Manage your subscription plan</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium flex items-center gap-2">
                  Current Plan:
                  <Badge variant={isPro ? 'default' : 'secondary'}>
                    {isPro && <Crown className="h-3 w-3 mr-1" />}
                    {userProfile.plan === 'pro' ? 'Pro' : 'Free'}
                  </Badge>
                </p>
                {isPro && userProfile.subscription?.scheduledCancellation && userProfile.subscription?.cancelAt && (
                  <Badge variant="destructive" className="text-xs">
                    Cancels {format(userProfile.subscription.cancelAt, 'MMM dd, yyyy')}
                  </Badge>
                )}
              </div>
              {isPro && (
                <p className="text-sm text-muted-foreground mt-1">
                  {userProfile.subscription?.scheduledCancellation && userProfile.subscription?.cancelAt && `Pending Cancellation on ${format(userProfile.subscription.cancelAt, 'MMMM dd, yyyy')}`}
                  {userProfile.subscription?.scheduledCancellation && !userProfile.subscription?.cancelAt && userProfile.subscription?.currentPeriodEnd && `Pending Cancellation on ${format(userProfile.subscription.currentPeriodEnd, 'MMMM dd, yyyy')}`}
                  {!userProfile.subscription?.scheduledCancellation && userProfile.subscription?.currentPeriodEnd && `Renews on ${format(userProfile.subscription.currentPeriodEnd, 'MMMM dd, yyyy')}`}
                </p>
              )}
            </div>
            <Button onClick={() => navigate('/subscription')}>
              {isPro ? 'Manage Subscription' : 'Upgrade to Pro'}
            </Button>
          </div>

          {!isPro && (
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm">
                Upgrade to Pro for saved Business, Customer, and Inventory profiles (auto-fill in seconds), Word document templates, and priority support!
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Predefined Taxes Section */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Predefined Taxes</CardTitle>
              <CardDescription>
                Configure standard tax rates to auto-fill your invoices
              </CardDescription>
            </div>
            <Button
              variant="outline"
              disabled={!isPro}
              onClick={handleAddTax}
            >
              <Plus className="h-4 w-4 mr-2" />
              Tax Setup
              {!isPro && (
                <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1 font-semibold">PRO</Badge>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!isPro ? (
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Upgrade to Pro to save predefined tax rates (e.g. VAT, GST, SST) for quick selection in your invoices.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {(!userProfile?.predefinedTaxes || userProfile.predefinedTaxes.length === 0) ? (
                <p className="text-sm text-muted-foreground">No predefined taxes configured. Click "Tax Setup" to add one.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userProfile.predefinedTaxes.map((tax: Tax, index: number) => (
                    <Card key={index} className="border">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold">{tax.label}</h4>
                            <p className="text-sm text-muted-foreground space-y-1 mt-1">
                              Type: {tax.type === 'percentage' ? 'Percentage' : tax.type === 'fixed' ? 'Fixed' : tax.type.toUpperCase()} • Value: {tax.type === 'fixed' ? formatCurrency(Number(tax.value) || 0, userProfile?.defaultCurrency || 'USD') : `${tax.value}%`}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditTax(index)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTax(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Business Profiles Section */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Business Profiles</CardTitle>
              <CardDescription>
                Manage your business information for invoices
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              disabled={!isPro}
              onClick={handleAddBusiness}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Business
              {!isPro && (
                <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1 font-semibold">PRO</Badge>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!isPro && businesses.length === 0 ? (
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Upgrade to Pro to add multiple business profiles.
              </p>
            </div>
          ) : !isPro && businesses.length > 0 ? (
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-4 rounded-lg mb-4">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  ⚠️ Your Pro subscription has ended. You can view your business profiles below, but need to upgrade to Pro to add, edit, or delete them.
                </p>
              </div>
              {businesses.map((business) => (
                <Card key={business.businessId} className="border opacity-75">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{business.name}</h4>
                          {business.isDefault && (
                            <Badge variant="secondary" className="text-xs">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          {business.email && <p>Email: {business.email}</p>}
                          {business.phone && <p>Phone: {business.phone}</p>}
                          {business.address && <p>Address: {business.address}</p>}
                          {business.taxNumber && <p>Tax Number: {business.taxNumber}</p>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : businesses.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No business profiles yet</p>
              <Button onClick={handleAddBusiness}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Business
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {businesses.map((business) => (
                <Card key={business.businessId} className="border">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{business.name}</h4>
                          {business.isDefault && (
                            <Badge variant="secondary" className="text-xs">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          {business.email && <p>Email: {business.email}</p>}
                          {business.phone && <p>Phone: {business.phone}</p>}
                          {business.address && <p>Address: {business.address}</p>}
                          {business.taxNumber && <p>Tax Number: {business.taxNumber}</p>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditBusiness(business)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteBusiness(business.businessId)}
                          disabled={business.isDefault && businesses.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Profiles Section */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Customer Profiles</CardTitle>
              <CardDescription>
                Save customer information for quick invoice creation
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              disabled={!isPro}
              onClick={handleAddCustomer}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Customer
              {!isPro && (
                <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1 font-semibold">PRO</Badge>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!isPro && customers.length === 0 ? (
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Upgrade to Pro to save customer profiles.
              </p>
            </div>
          ) : !isPro && customers.length > 0 ? (
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-4 rounded-lg mb-4">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  ⚠️ Your Pro subscription has ended. You can view your customer profiles below, but need to upgrade to Pro to add, edit, or delete them.
                </p>
              </div>
              {customers.map((customer) => (
                <Card key={customer.customerId} className="border opacity-75">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{customer.name}</h4>
                          {customer.isDefault && (
                            <Badge variant="secondary" className="text-xs">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          {customer.companyName && <p>Company: {customer.companyName}</p>}
                          {customer.email && <p>Email: {customer.email}</p>}
                          {customer.phone && <p>Phone: {customer.phone}</p>}
                          {customer.address && <p>Address: {customer.address}</p>}
                          {customer.taxNumber && <p>Tax Number: {customer.taxNumber}</p>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No customer profiles yet</p>
              <Button onClick={handleAddCustomer}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Customer
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {customers.map((customer) => (
                <Card key={customer.customerId} className="border">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{customer.name}</h4>
                          {customer.isDefault && (
                            <Badge variant="secondary" className="text-xs">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          {customer.companyName && <p>Company: {customer.companyName}</p>}
                          {customer.email && <p>Email: {customer.email}</p>}
                          {customer.phone && <p>Phone: {customer.phone}</p>}
                          {customer.address && <p>Address: {customer.address}</p>}
                          {customer.taxNumber && <p>Tax Number: {customer.taxNumber}</p>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditCustomer(customer)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCustomer(customer.customerId)}
                          disabled={customer.isDefault && customers.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Item Inventory Section */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-indigo-500" />
                <CardTitle>Inventory Items & Services</CardTitle>
              </div>
              <CardDescription>
                Save reusable items, services, codes, and pricing for single-click invoice lines
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              disabled={!isPro}
              onClick={handleAddItem}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Item / Service
              {!isPro && (
                <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1 font-semibold">PRO</Badge>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!isPro && inventoryItems.length === 0 ? (
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Upgrade to Pro to build and manage your inventory items and services database.
              </p>
            </div>
          ) : !isPro && inventoryItems.length > 0 ? (
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-4 rounded-lg mb-4">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  ⚠️ Your Pro subscription has ended. You can view your database below, but need to upgrade to Pro to add, edit, or delete items and services.
                </p>
              </div>
              {inventoryItems.map((item) => (
                <Card key={item.itemId} className="border opacity-75">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{item.name}</h4>
                          <Badge variant="outline" className="text-xs">{item.code}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Price: {formatCurrency(Number(item.rate) || 0, userProfile?.defaultCurrency || 'USD')} per {item.unit}</p>
                          {item.description && <p>Description: {item.description}</p>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : inventoryItems.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No items or services saved yet</p>
              <Button onClick={handleAddItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Item
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {inventoryItems.map((item) => (
                <Card key={item.itemId} className="border">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{item.name}</h4>
                          <Badge variant="outline" className="text-xs">{item.code}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Price: {formatCurrency(Number(item.rate) || 0, userProfile?.defaultCurrency || 'USD')} per {item.unit}</p>
                          {item.description && <p>Description: {item.description}</p>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditItem(item)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteItem(item.itemId)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-500" />
            <CardTitle>Two-Factor Authentication (2FA)</CardTitle>
          </div>
          <CardDescription>
            Secure your account with time-based one-time passcodes from an authenticator app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {userProfile.mfaEnabled ? (
            <div className="border border-green-200 bg-green-50/20 rounded-lg p-6 space-y-4 text-left">
              <div className="flex items-start gap-3">
                <Crown className="h-6 w-6 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-900 text-lg">Two-Factor Authentication is Enabled</h3>
                  <p className="text-sm text-green-700 mt-1">
                    Your account is protected. Each time you sign in to your account, you will be required to provide the 6-digit verification code.
                  </p>
                </div>
              </div>
              <div className="pt-2">
                <Button 
                  variant="destructive" 
                  onClick={handleDisableMfa} 
                  disabled={mfaLoading}
                >
                  Disable Two-Factor Authentication
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6 text-left">
              {setupStep === 'idle' ? (
                <div className="border border-gray-150 rounded-lg p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-amber-500 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">Two-Factor Authentication is Disabled</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        We recommend enabling 2FA to protect your account from unauthorized access. Click the button below to start setup.
                      </p>
                    </div>
                  </div>
                  <div className="pt-2">
                    <Button onClick={handleStartMfaSetup} disabled={mfaLoading}>
                      Enable 2FA Protection
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border border-indigo-100 bg-indigo-50/10 rounded-lg p-6 space-y-6">
                  <h3 className="font-semibold text-gray-900 text-lg border-b pb-2">
                    Set Up Authenticator (Step {setupStep === 'scan' ? '1: Scan QR Code' : '2: Verify Code'})
                  </h3>

                  {setupStep === 'scan' && (
                    <div className="flex flex-col md:flex-row items-center gap-6">
                      <div className="bg-white p-3 border rounded-lg shadow-sm">
                        {mfaSecret && (
                          <img 
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                              `otpauth://totp/QuickFreeInvoice:${user.email}?secret=${mfaSecret}&issuer=QuickFreeInvoice`
                            )}`}
                            alt="Scan to add TOTP"
                            className="h-44 w-44"
                          />
                        )}
                      </div>
                      <div className="space-y-3 flex-1 text-sm text-gray-600">
                        <p className="font-medium text-gray-900 text-left">Follow these steps:</p>
                        <ol className="list-decimal list-inside space-y-2 pl-1 text-left">
                          <li>Open Google Authenticator or another TOTP app.</li>
                          <li>Scan the QR code shown here, or manually add a key.</li>
                          {mfaSecret && (
                            <li>
                              Manual Secret: <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono font-bold text-indigo-700 select-all">{mfaSecret}</code>
                            </li>
                          )}
                        </ol>
                        <div className="pt-2 text-left">
                          <Button onClick={() => setSetupStep('verify')}>
                            Next: Verify Code
                          </Button>
                          <Button variant="ghost" className="ml-2" onClick={handleCancelSetup}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {setupStep === 'verify' && (
                    <div className="space-y-4 max-w-sm text-left">
                      <div className="space-y-2">
                        <Label htmlFor="setupCode">Enter 6-Digit Verification Code</Label>
                        <Input
                          id="setupCode"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          placeholder="000000"
                          className="text-center font-mono text-xl tracking-widest"
                          value={totpCode}
                          onChange={(e) => {
                            setTotpCode(e.target.value.replace(/\D/g, ''));
                            setTotpError(null);
                          }}
                          autoFocus
                          required
                        />
                        {totpError && (
                          <p className="text-sm text-destructive flex items-center gap-1 mt-1">
                            <AlertTriangle className="h-4 w-4" />
                            {totpError}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleConfirmMfaSetup} disabled={mfaLoading}>
                          {mfaLoading ? 'Activating...' : 'Confirm & Enable 2FA'}
                        </Button>
                        <Button variant="outline" onClick={() => { setSetupStep('scan'); setTotpCode(''); }}>
                          Back
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Support Tickets Console Section */}
      <Card className="mb-6 animate-in fade-in slide-in-from-bottom-3 duration-300" id="support-tickets">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Support & Help Desk
          </CardTitle>
          <CardDescription>
            Submit an issue report, attach screenshots, and communicate directly with our support team.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SupportTicketConsole />
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Irreversible actions that will permanently affect your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-semibold">Delete Account</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Permanently delete your account and all associated data. This action cannot be undone.
                  All invoices, business profiles, and uploaded files will be deleted.
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete Account
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Account
            </DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone. All your data will be permanently deleted:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>All invoices and invoice history</li>
              <li>All business profiles</li>
              <li>Uploaded logos and files</li>
              <li>Account information and settings</li>
              <li>Subscription data (if applicable)</li>
            </ul>

            <div className="space-y-2">
              <Label>Type "DELETE" to confirm</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="font-mono"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteConfirmText('');
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || deleting}
            >
              {deleting ? 'Deleting...' : 'Delete My Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Business Dialog */}
      <Dialog open={showBusinessDialog} onOpenChange={setShowBusinessDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBusiness ? 'Edit Business Profile' : 'Add Business Profile'}
            </DialogTitle>
            <DialogDescription>
              {editingBusiness 
                ? 'Update your business information' 
                : 'Add a new business profile for your invoices'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Logo Upload Section */}
            <div className="space-y-2 border-b pb-4">
              <Label>Business Logo</Label>
              {!isPro ? (
                <div className="border border-dashed rounded-lg p-4 bg-slate-50 flex items-center justify-between text-left">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0">
                      <Crown className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 text-sm">Business Logo (Pro Feature)</h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Upgrade to Pro to upload logo and brand invoices/quotations.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold shrink-0"
                    onClick={() => {
                      setShowBusinessDialog(false);
                      navigate('/subscription');
                    }}
                  >
                    Upgrade
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-4 items-center">
                  {businessForm.logoUrl ? (
                    <div className="relative group w-32 h-20 bg-slate-100 border rounded-lg overflow-hidden flex items-center justify-center">
                      <img
                        src={businessForm.logoUrl}
                        alt="Logo preview"
                        className="max-h-full max-w-full object-contain"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-semibold"
                      >
                        Remove Logo
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-20 bg-slate-50 border border-dashed rounded-lg flex items-center justify-center text-muted-foreground">
                      <ImageIcon className="w-8 h-8 opacity-45" />
                    </div>
                  )}

                  <div className="flex-1 space-y-2 text-left">
                    <div className="flex items-center gap-2">
                      <Input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={logoUploading}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={logoUploading}
                        onClick={() => document.getElementById('logo-upload')?.click()}
                      >
                        {logoUploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            {businessForm.logoUrl ? 'Change Logo' : 'Upload Logo'}
                          </>
                        )}
                      </Button>
                      {businessForm.logoUrl && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={handleRemoveLogo}
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Supported formats: PNG, JPG, SVG. Max size: 2MB. Recommended: Landscape ratio.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="businessName">
                  Business Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="businessName"
                  value={businessForm.name}
                  onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                  placeholder="Your Company Name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessEmail">
                  Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="businessEmail"
                  type="email"
                  value={businessForm.email}
                  onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
                  placeholder="contact@company.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessPhone">Phone</Label>
                <Input
                  id="businessPhone"
                  value={businessForm.phone}
                  onChange={(e) => setBusinessForm({ ...businessForm, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessWebsite">Website</Label>
                <Input
                  id="businessWebsite"
                  value={businessForm.website}
                  onChange={(e) => setBusinessForm({ ...businessForm, website: e.target.value })}
                  placeholder="https://yourcompany.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessAddress">Address</Label>
              <Textarea
                id="businessAddress"
                value={businessForm.address}
                onChange={(e) => setBusinessForm({ ...businessForm, address: e.target.value })}
                placeholder="123 Business Street, City, State, ZIP"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="businessTaxNumber">Tax Number</Label>
                <Input
                  id="businessTaxNumber"
                  value={businessForm.taxNumber}
                  onChange={(e) => setBusinessForm({ ...businessForm, taxNumber: e.target.value })}
                  placeholder="Tax ID / VAT Number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessBankDetails">Bank Details</Label>
                <Input
                  id="businessBankDetails"
                  value={businessForm.bankDetails}
                  onChange={(e) => setBusinessForm({ ...businessForm, bankDetails: e.target.value })}
                  placeholder="Bank account info"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="stripePaymentLink" className="flex items-center justify-between">
                  <span>Stripe Payment Link</span>
                  {userProfile?.plan !== 'pro' && (
                    <Badge variant="secondary" className="text-[10px] bg-amber-100 hover:bg-amber-100 text-amber-800 border-amber-200 py-0 px-1.5 font-semibold">PRO Only</Badge>
                  )}
                </Label>
                <Input
                  id="stripePaymentLink"
                  value={(businessForm as any).stripePaymentLink || ''}
                  onChange={(e) => setBusinessForm({ ...businessForm, stripePaymentLink: e.target.value })}
                  placeholder="https://buy.stripe.com/..."
                  disabled={userProfile?.plan !== 'pro'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="paypalLink" className="flex items-center justify-between">
                  <span>PayPal.me Link</span>
                  {userProfile?.plan !== 'pro' && (
                    <Badge variant="secondary" className="text-[10px] bg-amber-100 hover:bg-amber-100 text-amber-800 border-amber-200 py-0 px-1.5 font-semibold">PRO Only</Badge>
                  )}
                </Label>
                <Input
                  id="paypalLink"
                  value={(businessForm as any).paypalLink || ''}
                  onChange={(e) => setBusinessForm({ ...businessForm, paypalLink: e.target.value })}
                  placeholder="https://paypal.me/..."
                  disabled={userProfile?.plan !== 'pro'}
                />
              </div>
            </div>

            {(() => {
              const hasPaymentLinks = !!(
                (businessForm as any).stripePaymentLink?.trim() ||
                (businessForm as any).paypalLink?.trim()
              );
              const isCheckboxDisabled = userProfile?.plan !== 'pro' || !hasPaymentLinks;
              return (
                <div className="flex items-center space-x-2 py-1">
                  <input
                    type="checkbox"
                    id="enableAutoApplyPaymentLinks"
                    checked={!isCheckboxDisabled && !((businessForm as any).disableAutoApplyPaymentLinks)}
                    onChange={(e) => setBusinessForm({ ...businessForm, disableAutoApplyPaymentLinks: !e.target.checked })}
                    disabled={isCheckboxDisabled}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  />
                  <Label
                    htmlFor="enableAutoApplyPaymentLinks"
                    className={`text-xs font-normal cursor-pointer flex items-center gap-1.5 ${isCheckboxDisabled ? 'text-slate-400' : 'text-slate-600'}`}
                  >
                    Enable auto-apply payment links on new invoices
                    {userProfile?.plan !== 'pro' && (
                      <Badge variant="secondary" className="text-[9px] bg-amber-100 text-amber-800 border-amber-200 py-0 px-1 font-semibold">PRO</Badge>
                    )}
                    {userProfile?.plan === 'pro' && !hasPaymentLinks && (
                      <span className="text-[10px] text-slate-400 italic">(add a payment link to enable)</span>
                    )}
                  </Label>
                </div>
              );
            })()}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={businessForm.isDefault}
                onChange={(e) => setBusinessForm({ ...businessForm, isDefault: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="isDefault" className="cursor-pointer">
                Set as default business profile
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBusinessDialog(false)}
              disabled={savingBusiness}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveBusiness}
              disabled={!businessForm.name || !businessForm.email || savingBusiness}
            >
              {savingBusiness ? 'Saving...' : editingBusiness ? 'Update Business' : 'Add Business'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? 'Edit Customer Profile' : 'Add Customer Profile'}
            </DialogTitle>
            <DialogDescription>
              {editingCustomer 
                ? 'Update customer information' 
                : 'Add a new customer profile for quick invoice creation'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customerName">
                  Customer Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="customerName"
                  value={customerForm.name}
                  onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerCompany">Company Name</Label>
                <Input
                  id="customerCompany"
                  value={customerForm.companyName}
                  onChange={(e) => setCustomerForm({ ...customerForm, companyName: e.target.value })}
                  placeholder="Customer Company Inc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerEmail">
                  Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="customerEmail"
                  type="email"
                  value={customerForm.email}
                  onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                  placeholder="customer@email.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerPhone">Phone</Label>
                <Input
                  id="customerPhone"
                  value={customerForm.phone}
                  onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="customerAddress">Address</Label>
              <Textarea
                id="customerAddress"
                value={customerForm.address}
                onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
                placeholder="123 Customer Street, City, State, ZIP"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customerTaxNumber">Tax Number</Label>
              <Input
                id="customerTaxNumber"
                value={customerForm.taxNumber}
                onChange={(e) => setCustomerForm({ ...customerForm, taxNumber: e.target.value })}
                placeholder="Tax ID / VAT Number"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="customerDefault"
                checked={customerForm.isDefault}
                onChange={(e) => setCustomerForm({ ...customerForm, isDefault: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="customerDefault" className="cursor-pointer">
                Set as default customer profile
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCustomerDialog(false)}
              disabled={savingCustomer}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveCustomer}
              disabled={!customerForm.name || !customerForm.email || savingCustomer}
            >
              {savingCustomer ? 'Saving...' : editingCustomer ? 'Update Customer' : 'Add Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Inventory Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit Item / Service' : 'Add Item / Service'}
            </DialogTitle>
            <DialogDescription>
              {editingItem 
                ? 'Update item or service details in your database' 
                : 'Add a new item or service to your database for single-click invoice creation'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="itemCode">
                  Item Code / SKU <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="itemCode"
                  value={itemForm.code}
                  onChange={(e) => setItemForm({ ...itemForm, code: e.target.value })}
                  placeholder="e.g. SRV-001, ITEM-A"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="itemName">
                  Item Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="itemName"
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  placeholder="e.g. Consultant Fee, Web Development"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="itemRate">
                  Unit Price / Rate ($) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="itemRate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={itemForm.rate ?? ''}
                  onChange={(e) => setItemForm({ ...itemForm, rate: e.target.value })}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="itemUnit">Unit</Label>
                <Input
                  id="itemUnit"
                  value={itemForm.unit}
                  onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                  placeholder="e.g. pcs, hrs, days"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="itemDescription">Description / Default Notes</Label>
              <Textarea
                id="itemDescription"
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                placeholder="Detailed description of the item"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowItemDialog(false)}
              disabled={savingItem}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveItem}
              disabled={!itemForm.code || !itemForm.name || savingItem}
            >
              {savingItem ? 'Saving...' : editingItem ? 'Update Item' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tax Predefined Setup Dialog */}
      <Dialog open={showTaxDialog} onOpenChange={setShowTaxDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTaxIndex !== null ? 'Edit Predefined Tax Rate' : 'Add Predefined Tax Rate'}
            </DialogTitle>
            <DialogDescription>
              Set up a saved tax profile to quickly apply to your invoices and quotations.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="taxLabel">
                Tax Label / Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="taxLabel"
                value={taxForm.label}
                onChange={(e) => setTaxForm({ ...taxForm, label: e.target.value })}
                placeholder="e.g. VAT 10%, SST 8%"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="taxType">Tax Type</Label>
                <Select
                  value={taxForm.type}
                  onValueChange={(val: any) => setTaxForm({ ...taxForm, type: val })}
                >
                  <SelectTrigger id="taxType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage %</SelectItem>
                    <SelectItem value="vat">VAT %</SelectItem>
                    <SelectItem value="sst">SST %</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="taxValue">
                  Rate / Value <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="taxValue"
                  type="number"
                  step="0.01"
                  min="0"
                  value={taxForm.value}
                  onChange={(e) => setTaxForm({ ...taxForm, value: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTaxDialog(false)}
              disabled={savingTax}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveTax}
              disabled={!taxForm.label || taxForm.value === '' || savingTax}
            >
              {savingTax ? 'Saving...' : editingTaxIndex !== null ? 'Update Tax' : 'Add Tax'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
